import { EST_TROUSSEAU } from '@/api/client'
import { amorcerSessionDepuisSlot } from '@/api/auth'
import { adopterLeCompteDeCetOnglet } from './adoption'
import { consommerRetour } from './retourLancement'

/**
 * L'AMORÇAGE de session au démarrage (BBG-233) — et l'état que la garde de
 * routes doit savoir attendre.
 *
 * ─── L'arbitrage, et pourquoi c'est celui-là ─────────────────────────────────
 *
 * En mode trousseau, une page rechargée ne sait plus qui elle est tant que
 * personne n'est allé chercher le slot actif et n'a racheté une preuve. Deux
 * conduites étaient possibles.
 *
 * Bloquer le premier rendu aurait été plus simple : on attend, puis on peint.
 * Mais `main.ts` porte un parti pris explicite et argumenté — « attendre le
 * réseau pour peindre le premier écran est ce qui donne l'impression d'une
 * application lente » — et le trahir ferait payer un aller-retour à CHAQUE
 * chargement, y compris à ceux qui n'ont aucune session à retrouver. Pire :
 * une panne du BFF retarderait alors tout démarrage, pour rien.
 *
 * On peint donc d'abord, et la garde de routes attend l'issue de l'amorçage
 * AVANT de trancher. C'est la seule façon d'éviter le défaut que les
 * utilisateurs signalent comme un bug : l'écran de connexion qui apparaît une
 * fraction de seconde puis disparaît, parce que la garde a jugé sur un état
 * encore vide.
 *
 * ─── Une panne n'est pas un verdict ──────────────────────────────────────────
 *
 * L'amorçage ne DÉCIDE rien : il tente, et se termine. S'il échoue, la session
 * reste absente et la garde enverra vers l'écran de connexion — mais elle le
 * fera parce qu'il n'y a pas de session, jamais parce qu'un réseau a hoqueté.
 * C'est pourquoi l'échec est avalé ICI, silencieusement : le seul fait qui
 * compte ensuite est « une session existe, ou non », et le serveur reste seul
 * juge de sa validité à la première requête.
 */

/** L'amorçage en cours, ou null quand il est fini (ou n'a jamais eu lieu). */
let enCours: Promise<void> | null = null

/** Vrai dès que l'amorçage a été tenté — une fois par chargement de page. */
let dejaTente = false

/**
 * Lance l'amorçage, une seule fois par chargement.
 *
 * Ne rend PAS la promesse : personne ne doit pouvoir attendre autre chose que
 * `attendreAmorcage()`, dont le contrat est justement de ne jamais rejeter.
 */
export function demarrerAmorcage(): void {
  if (dejaTente) return
  dejaTente = true
  /*
   * LE RETOUR DE LA PORTE PASSE EN PREMIER.
   *
   * On revient de la page de connexion avec un laissez-passer dans le fragment.
   * Il faut l'échanger AVANT que la garde de routes ne tranche, sinon elle
   * constate « pas de session », renvoie à la porte — qui renverrait ici, et
   * ainsi de suite. C'est pourquoi la consommation vit ICI et pas dans un
   * écran : la garde attend déjà l'issue de l'amorçage, et c'est le seul
   * endroit qu'elle attend.
   *
   * Un échec n'est pas masqué mais ne bloque rien : un laissez-passer rejoué
   * (rechargement d'un onglet resté ouvert) ou expiré laisse simplement sans
   * session, et la garde renvoie à la porte — ce qui est le bon geste.
   */
  enCours = consommerRetour()
    .then((ouverte) => {
      // Session ouverte par le retour : plus rien à amorcer.
      if (ouverte || !EST_TROUSSEAU) return undefined
      return amorcerSessionDepuisSlot().then(() => undefined)
    })
    /*
     * L'onglet adopte ENSUITE le compte avec lequel il vient d'entrer.
     *
     * Sans ce geste, `runtime.currentSession()` reste `null` et la surveillance
     * du trousseau ne regarde jamais rien : une déconnexion venue d'une autre
     * application passait inaperçue ici. Voir `adoption.ts`.
     *
     * Après l'ouverture de session, et jamais avant : le choix du slot se fait
     * sur l'adresse de la session en place, qui n'existe pas encore plus tôt.
     */
    .then(() => adopterLeCompteDeCetOnglet())
    .catch(() => {
      // Silence VOLONTAIRE : voir l'en-tête. Un échec d'amorçage n'est pas une
      // décision d'authentification, c'est une absence de session — que la
      // garde constatera d'elle-même.
      return undefined
    })
    .finally(() => {
      enCours = null
    })
}

/**
 * Attend que l'amorçage soit retombé, s'il court encore.
 *
 * Ne rejette JAMAIS : la garde de routes n'a pas à gérer d'erreur, elle a à
 * lire un état stabilisé. Résout immédiatement quand il n'y a rien à attendre —
 * mode `bearer`, amorçage déjà terminé, ou navigation ultérieure.
 */
export async function attendreAmorcage(): Promise<void> {
  if (enCours !== null) await enCours
}

/** Remise à zéro — réservée aux tests, qui doivent partir d'un état nu. */
export function reinitialiserAmorcagePourTests(): void {
  enCours = null
  dejaTente = false
}
